// App Check — MONITORING ONLY (#194).
//
// This makes the client attach an attestation token to its Firebase calls so
// the console can report how much traffic is unattested. Nothing is enforced:
// every service is left UNENFORCED server-side, so a missing or rejected token
// changes nothing for the user. Enforcement is a separate, later decision that
// must not be taken until the monitoring numbers are in — turning it on blocks
// any client that doesn't send a token, including installed PWAs running an
// older bundle.
//
// The site key is a reCAPTCHA Enterprise *score* key. Site keys are public by
// design (they ship in every page that uses reCAPTCHA), so it is inlined rather
// than routed through a gitignored .env file that worktree builds keep missing.
//
// NATIVE (Capacitor WebView, #2372 / #467): the reCAPTCHA provider is NEVER
// created. Its web key is not authorised for the native origin, so every token
// exchange would fail — harmless while nothing is enforced, but once the server
// enforces (#2371) it would look like a login problem, not an App Check one.
// A native build instead uses a CustomProvider whose token comes from the
// platform adapter (adapters/appCheck.js → App Attest / DeviceCheck / Play
// Integrity). Until the native plugin is linked (#2376) the adapter reports
// "unavailable": initAppCheck then returns null and logs a console.warn — a
// visible gap, never a throw and never a silent one. Web behaviour is unchanged.

import { initializeAppCheck, ReCaptchaEnterpriseProvider, CustomProvider } from 'firebase/app-check'

import { APP_ENV } from '../environment'
import { isNativePlatform } from '../platform/runtime'
import { getAppCheckToken, isAppCheckTokenAvailable } from '../platform/adapters/appCheck'

// A site key belongs to ONE Firebase project, so a project cannot borrow
// another's — it would fail every token exchange and fill the console with
// noise (#183). An environment with no key of its own simply runs without App
// Check, which is harmless while enforcement is off everywhere.
const SITE_KEY_BY_ENV = {
  production: '6Lc_tGktAAAAAGRwdicuswYtblfKBAw8J47A7LMO',
  staging: '6LcNeYAtAAAAAOws6Na7PBSS8dgW3VeSWGeyMuiP',
}

export const APP_CHECK_SITE_KEY = SITE_KEY_BY_ENV[APP_ENV] || ''

/**
 * Start App Check for the given Firebase app. Best-effort: any failure here
 * (blocked reCAPTCHA script, offline, unsupported browser) must never stop the
 * app from loading, because nothing depends on the token while enforcement is
 * off.
 * @param {object} app - The initialized Firebase app.
 * @param {object} [options]
 * @param {boolean} [options.enabled] - Pass false to skip (emulators, tests).
 * @return {object|null} The App Check instance, or null when skipped/failed.
 */
export function initAppCheck(app, { enabled = true } = {}) {
  if (!enabled || typeof window === 'undefined' || !APP_CHECK_SITE_KEY) {
    return null
  }

  if (isNativePlatform() && !isAppCheckTokenAvailable()) {
    console.warn('[appCheck] native App Check plugin is not available — running without App Check tokens')
    return null
  }

  try {
    const provider = isNativePlatform()
      ? new CustomProvider({ getToken: getAppCheckToken })
      : new ReCaptchaEnterpriseProvider(APP_CHECK_SITE_KEY)
    return initializeAppCheck(app, {
      provider,
      isTokenAutoRefreshEnabled: true,
    })
  } catch (err) {
    console.warn('[appCheck] initialization skipped', err)
    return null
  }
}
